import React, { useState, useEffect } from 'react';
import { ReptoolModal } from './ReptoolModal.tsx';
import { PhotoUploader } from './PhotoUploader.tsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faPen, faQuestion, faSave, faSync, faTimes } from '@fortawesome/free-solid-svg-icons';
import { GeneInput, ToggleSwitch } from './GeneInput.tsx';
import { BasicFetch } from '../../utils/BasicFetch.ts';
import { Carousel } from 'react-responsive-carousel';
import { MorphGene, Morph, blankMorphGene } from '../../types/Morph.ts';
import { UserInfo } from '../../types/UserInfo.ts';
import { LoadingSpinner } from '../Loading.tsx';
import { driver } from 'driver.js';
import { Table } from 'react-bootstrap';
import { Gene } from '../../types/Gene.ts';
import Lightbox from "yet-another-react-lightbox";
import Captions from "yet-another-react-lightbox/plugins/captions";

interface PriceData {
  average_price: number
  count: number
  max_price: number
  min_price: number
  total: number
}

const removeExtraText = (name: string) => {
  if (name) {
    name = name.replace(" (Line Bred)", "");
    name = name.replace(" (Unproven)", "");
  }
  return name;
}

const GeneItem = ({
  gene,
  setSelectedGene,
  readOnly = false,
  setCurrentMorph,
  genes
}: {
  gene: MorphGene,
  setSelectedGene: (gene: MorphGene) => void,
  readOnly?: boolean,
  setCurrentMorph: (oldGene: MorphGene, newGene: MorphGene) => void,
  genes: Gene[]
}) => {
  const [currentGene, setCurrentGene] = useState(gene);
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    setCurrentGene(gene);
  }, [gene])
  return (
    <li onClick={() => { setSelectedGene(gene) }} style={{ borderStyle: !!gene.gene_name ? 'solid' : 'dashed' }}>
      <h6>{currentGene.gene_type || 'Unproven'}{currentGene.is_super ? " (Homozygous)" : ""}</h6>
      <div className="d-flex flex-row justify-content-center">
        {!editing && <div className="morph-trait">
          {!currentGene.gene_name && !readOnly ? <FontAwesomeIcon icon={faQuestion} /> : currentGene.gene_name}
        </div>}
        {editing && <div className='d-flex flex-column'>
          <GeneInput
            id={currentGene.id}
            gene={currentGene}
            setGene={(gene) => { setCurrentGene({ ...gene, is_super: false }); }}
            genes={genes}
          />
          <ToggleSwitch
            homCheck={currentGene.is_super}
            selectedGene={currentGene}
            setHomCheck={(value) => setCurrentGene({ ...currentGene, is_super: value })}
          />
        </div>}
        {!readOnly && <div className='ml-2 my-auto h-100'>
          <div className='button btn-sm' onClick={() => setEditing(!editing)}>
            {editing ? <FontAwesomeIcon onClick={() => setCurrentMorph(gene, currentGene)} icon={faSave} /> : <FontAwesomeIcon icon={faPen} />}
          </div>
          {editing && <div className='button btn-sm mt-2' onClick={() => setCurrentMorph(gene, { ...blankMorphGene })}>
            <FontAwesomeIcon icon={faTimes} />
          </div>}
        </div>}
      </div>
    </li>
  )
}

const UploadSlide = ({
  morphName,
  backToMorph,
  userInfo,
  setShowHelp
}: {
  morphName: string,
  backToMorph: Function,
  userInfo: UserInfo,
  setShowHelp: Function
}) => {
  const [currentMorph, setCurrentMorph] = useState<string>(morphName);
  const [currentPhotoCredit, setCurrentPhotoCredit] = useState<string>(userInfo.breeder_credit);
  const [currentPhoto, setCurrentPhoto] = useState<Blob | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);


  const uploadImage = () => {
    if (!currentPhoto) {
      setError("No photo selected");
      return;
    }
    if (!currentMorph) {
      setError("Morph name required");
      return;
    }


    let formData = new FormData();
    let filename = `${currentMorph}-${new Date().toISOString()}.jpg`

    formData.append("file", currentPhoto, filename);
    formData.append("reptile", localStorage.getItem('selectedReptile'));
    formData.append("morph", currentMorph);
    formData.append("breeder_credit", currentPhotoCredit);

    BasicFetch({
      url: `${(window as any).serverLocation}/reptile/add_picture`,
      data: formData,
      method: 'POST',
      contentType: 'multipart/form-data',
      onSuccess: (data) => {
        window.alert("Successfully submitted picture for approval.")
        setCurrentPhoto(null);
      },
      onFail: () => {
        window.alert("There was a problem submitting your photo. Please try again later.")
      },
      onFinish: () => {
        setUploading(false);
      }
    })
  }

  useEffect(() => {
    if (uploading) {
      uploadImage();
    }
  }, [uploading]);

  useEffect(() => {
    setCurrentMorph(morphName)
  }, [morphName])

  return (
    <div id="uploader-slide" className={`mt-5 p-4`}>
      <div className="carousel slide" data-interval="false" data-ride="carousel">
        <h4>Upload your photos for a morph</h4>
        <div id='guide-lines' className='text-primary' onClick={() => setShowHelp(true)}>
          Guidelines
        </div>
        <div id="morph-name" className="mt-3 has-validation d-flex flex-column">
          <p>Morph</p>
          <input
            type="text"
            className='form-control'
            value={currentMorph}
            onChange={(event) => setCurrentMorph(event.target.value)}
          />
          <div className='invalid-feedback'>Morph name required</div>
        </div>
        <form id="photo-upload-form">

          <div id='photo-credit' className="mt-3">
            <p>Photo Credit</p>
            <input
              id="photo-credit-input"
              className="form-control glow form-control-dark rounded"
              type="text"
              value={currentPhotoCredit}
              onChange={(event) => { setCurrentPhotoCredit(event.target.value) }}
            />
          </div>
          <div className='my-4'>
            <PhotoUploader setUploadPhoto={(data) => setCurrentPhoto(data)} clearPhoto={!currentPhoto} />
          </div>
          {error && <div className="alert alert-danger mt-3" role="alert">{error}</div>}
          <div className="d-flex flex-row mt-3">
            {!uploading ?
              <input
                type="submit"
                id="submit-btn"
                onClick={(event) => { event.preventDefault(); setUploading(true) }}
                className="bg-black text-blue button glow btn"
                value="Submit Photo" disabled={!currentPhoto}
              />
              : <div id="upload-spinner" >
                <FontAwesomeIcon icon={faSync} size="2x" spin className='my-auto ml-2' />
              </div>}
          </div>
        </form>

      </div>
      <div className="button mt-5 d-flex flex-row justify-content-center" onClick={() => backToMorph()}>
        Back
        <FontAwesomeIcon icon={faArrowRight} className='my-auto ml-2' />
      </div>
    </div>
  )
}

const DetailSlide = ({
  morph,
  setSelectedGene = () => { },
  backToMorph,
  setUpload,
  readOnly = true
}: {
  morph: Morph,
  setSelectedGene: Function,
  backToMorph?: Function,
  setUpload: Function
  readOnly?: boolean
}) => {
  const subSections = ["genes", "value"];
  const pictureSlides = morph.pictures.map((picture) => ({ src: picture.url, description: picture.credit }));
  const [openLightbox, setOpenLightbox] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [currentMorph, setCurrentMorph] = useState(morph);
  const defaultSubSection = subSections.findIndex((x) => x === localStorage.getItem('activeSubSection'));
  const [activeSubSection, setActiveSubSection] = useState(defaultSubSection !== -1 ? defaultSubSection : 0);
  const [value, setValue] = useState<PriceData | null>(null);
  const [loadingPrices, setLoadingPrices] = useState(false);
  const updateMorph = (oldGene, gene) => {
    const newMorph = { ...currentMorph };
    const newGenes = newMorph.genes.map((x) => x.gene_name === oldGene.gene_name ? gene : x);
    newMorph.genes = newGenes;
    setCurrentMorph(newMorph);
  }

  useEffect(() => {
    setLoadingPrices(true);
    setCurrentMorph(morph);
  }, [morph])
  useEffect(() => {
    if (loadingPrices) {
      getValue();
    }
  }, [loadingPrices])

  const saveChanges = () => {
    const data = { ...currentMorph, genes: currentMorph.genes?.filter((gene) => !!gene.gene_name) || [] }
    BasicFetch({
      url: `${(window as any).serverLocation}/morph/${currentMorph.id}/edit`,
      data: data,
      method: 'PATCH',
      onSuccess: (data) => {
        window.alert("Successfully updated morph.")
      },
      onFail: () => {
        window.alert("There was a problem updating your morph. Please try again later.")
      }
    })
  }
  const setInactive = () => {
    BasicFetch({
      url: `${(window as any).serverLocation}/morph/${currentMorph.id}/hide`,
      data: {},
      method: 'PATCH',
      onSuccess: (data) => {
        window.alert("Successfully hid morph.")
      },
      onFail: () => {
        window.alert("There was a problem updating your morph. Please try again later.")
      }
    })
  }
  const getValue = () => {
    BasicFetch({
      url: `${(window as any).serverLocation}/scraper/get_prices/${currentMorph.id}`,
      method: 'GET',
      onSuccess: (resp: { success: Boolean, data: PriceData }) => {
        setValue(resp.data);
        setLoadingPrices(false);
      },
      onFail: () => {
        setLoadingPrices(false);
      }
    });

  }

  return (
    morph ?
      <div>
        <Lightbox
          open={openLightbox}
          close={() => setOpenLightbox(false)}
          slides={pictureSlides}
          index={activeSlide}
          plugins={[Captions]}
        />
        <Carousel
          className='h-100'
          selectedItem={activeSlide}
          showIndicators={false}
          showStatus={true}
          showThumbs={false}
          onChange={(index) => setActiveSlide(index)}
        >
          {morph.pictures.length ? morph.pictures.map((picture, index) => (
            <div key={index}>
              <div
                className="w-100"
                onClick={() => setOpenLightbox(true)}
                style={{
                  backgroundImage: `url("${picture.url}")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center',
                  backgroundSize: 'contain',
                  height: '30vh'
                }}>
              </div>
              <p style={{ lineHeight: 1 }}><small className="breeder-overlay mt-2">{picture.credit}</small></p>
            </div>
          )) :
            <div>
              <div
                className="w-100"
                style={{
                  backgroundImage: `url('images/notavailable.jpg')`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center',
                  backgroundSize: 'contain',
                  height: '30vh'
                }}></div>
            </div>
          }
        </Carousel >
        <div id="info_container" className="">
          <div className="columns no-pad">
            <div className="collection-item columns no-pad">
              <div className="morph-container"></div>
              {backToMorph && <div className="btn-outline-light btn mb-2" onClick={() => { backToMorph(); setActiveSlide(0) }}>Back</div>}
              <h3 className="columns no-pad">{morph.name}</h3>
              <div id="price-gene-container" className="d-flex w-100 flex-column">

                <ul role="tablist" className="ui-tabs-nav ml-0 mb-3 mt-2 nav-tabs nav ui-corner-all ui-helper-reset ui-helper-clearfix ui-widget-header">
                  <li id="gene-label" onClick={() => { localStorage.setItem("morphdetailsubsection", subSections[0]); setActiveSubSection(0) }} className={`nav-item nav-item-lg ${activeSubSection === 0 ? 'ui-tabs-active' : ''} px-3 ui-state-active`}>
                    <h4><button type="button" className={`nav-link p-0  ${activeSubSection === 0 ? 'active' : ''}`} id="ui-id-1">Genes</button></h4>
                  </li>
                  <li id="value-label" onClick={() => { localStorage.setItem("morphdetailsubsection", subSections[1]); setActiveSubSection(1) }} className={`nav-item nav-item-lg px-3 ${activeSubSection === 1 ? 'ui-tabs-active' : ''} ui-state-active`}>
                    <h4><button type="button" className={`nav-link ${activeSubSection === 1 ? 'active' : ''} p-0`}>
                      Est. Value
                    </button></h4>
                  </li>
                </ul>
                {activeSubSection === 0 && <div id="genes">
                  <ul className='px-0'>
                    {currentMorph.genes.map((gene) => (
                      <GeneItem
                        key={gene.gene_name}
                        gene={gene}
                        setCurrentMorph={updateMorph}
                        setSelectedGene={(gene) => { setSelectedGene(gene) }}
                        readOnly={readOnly}
                      />
                    ))}
                    {!readOnly && <GeneItem
                      gene={{ ...blankMorphGene }}
                      setCurrentMorph={(oldGene, gene) => setCurrentMorph({ ...currentMorph, genes: [...currentMorph.genes, gene] })}
                      setSelectedGene={() => { }}
                      readOnly={false}
                    />}
                  </ul>
                </div>}
                {activeSubSection === 1 && <div id="value" className='px-3'>
                  {value && <div>
                    <Table variant='dark' className='text-light'>
                      <tbody>
                        <tr>
                          <th><h4>Low</h4></th>
                          <td><h4>${value?.min_price}</h4></td>
                        </tr>
                        <tr>
                          <th><h4>High</h4></th>
                          <td><h4>${value?.max_price}</h4></td>
                        </tr>
                        <tr>
                          <th><h4>Average</h4></th>
                          <td><h4>${(value?.average_price.toFixed(2))}</h4></td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>}
                  {loadingPrices && <LoadingSpinner />}

                  {!value && !loadingPrices && <div className="text-center">
                    <p>There are no listings found for this morph at this time.
                      <br />Please try again later</p>
                  </div>}
                  {!loadingPrices && <small>
                    Values are based on current MorphMarket listings.
                    <br />
                    Prices can vary based on age, sex, gene expression, and other factors.
                  </small>}
                </div>}

              </div>
              <hr />
              <div id="morph_text" className="no-pad">
                {morph.description && morph.description !== "No info available on this morph" && <React.Fragment>
                  <p>{morph.description || "Description not available."}</p>
                  <hr />
                </React.Fragment>}
                <p id='add-photo' className="text-center mt-3">
                  <button type="button" onClick={() => setUpload(morph.name)} className="button text-light">Add Additional Photo</button>
                </p>
              </div>
              {!readOnly && <div className='d-flex flex-row mt-4 justify-content-around'>
                <div className='button mr-2' onClick={() => { setInactive() }}>Deactivate</div>
                <div className='button' onClick={() => { saveChanges() }}>Save</div>
              </div>}
            </div>
          </div>
        </div>
      </div>
      : <React.Fragment></React.Fragment>
  )
}

export const MorphDetailModal = ({
  morph,
  morphs,
  closeModal,
  userInfo,
  readOnly
}: {
  morph: Morph,
  morphs: Morph[],
  closeModal: Function,
  userInfo: UserInfo,
  readOnly: boolean
}) => {
  const [selectedGene, setSelectedGene] = useState(morph.genes.length > 0 ? { ...morph.genes[0], gene_name: removeExtraText(morph.genes[0].gene_name) } : { gene_name: "" });
  const [uploadMorphName, setUploadMorphName] = useState(morph.name);
  const [showHelp, setShowHelp] = useState(false);
  const [activeIndex, setActiveIndex] = useState(1);
  const lastSlide = morph.genes.length > 1 ? 2 : 1;
  selectedGene.gene_name = removeExtraText(selectedGene.gene_name);
  const foundSelectedGeneMorph = morphs.find((x) => x.name === selectedGene.gene_name);

  const uploadSlideSteps = [
    { element: '#uploader-slide', popover: { title: 'Upload', description: 'Here you can upload your own photos for this morph. Please be sure to follow the guidelines for submitting photos.' } },
    { element: '#guide-lines', popover: { title: 'Guidelines', description: 'Here you can view the guidelines for submitting photos.' } },
    { element: '#photo-credit', popover: { title: 'Credit', description: 'RepTool uses community submitted photos or only photos used with permission. Everytime a photo is used we want to be sure to give credit to the photographer. This text will be displayed everytime a user sees your photo. Photos are to be approved by a moderator and you will be rewarded with RepTool scales!' } },
    { element: '#uploader', popover: { title: 'Uploader', description: 'This is the tool that will allow you to choose an image to be submitted. Don\'t forget to hit the \'Submit Photo\' button when finished!' } }
  ]

  const detailsSteps = [
    { element: '#price-gene-container', popover: { title: 'Genes', description: 'This is the list of genes that this morph is comprised of. You can tap each one to view what the individual gene looks like.' } },
    { element: '#price-gene-container', popover: { title: 'Estimated Value', description: 'This is the estimated value of the morph based on current MorphMarket listings. This table may be blank if there is no available listings.' } },
    { element: '#add-photo', popover: { title: 'Add Photo', description: 'Here you can add additional photos for this morph. Please be sure to follow the guidelines for submitting photos.' } },
  ]


  const driverObj = driver({
    steps: activeIndex === 0 ? uploadSlideSteps : detailsSteps,
  })

  return (
    <React.Fragment>

      {showHelp && <UploadHelpModal closeModal={() => setShowHelp(false)} />}
      <ReptoolModal id='morph-detail-modal' title={"Morph Details"} closeModal={closeModal} showTutorial={() => driverObj.drive()} body={(
        <Carousel
          className='h-100'
          showIndicators={false}
          swipeable={false}
          selectedItem={activeIndex}
          showStatus={false}
          showArrows={false}
          showThumbs={false}
        >
          <UploadSlide
            morphName={uploadMorphName}
            userInfo={userInfo}
            setShowHelp={setShowHelp}
            backToMorph={() => { setActiveIndex(1); }}
          />
          <DetailSlide
            morph={morph}
            readOnly={readOnly}
            setUpload={(morph) => { setUploadMorphName(morph); setActiveIndex(0); }}
            setSelectedGene={(gene) => { setSelectedGene({ ...gene, gene_name: removeExtraText(gene.gene_name) }); setActiveIndex(lastSlide); }}
          />
          {foundSelectedGeneMorph && <DetailSlide
            morph={foundSelectedGeneMorph}
            readOnly={readOnly}
            setUpload={(morph) => { setUploadMorphName(morph); setActiveIndex(0); }}
            backToMorph={(gene) => { setActiveIndex(1); }}
          />}
        </Carousel>
      )} />
    </React.Fragment>
  )
}

export const UploadHelpModal = ({ closeModal }) => {
  return (
    <ReptoolModal id="picture_requirements_modal" title={"FAQ"} closeModal={closeModal} body={(
      <React.Fragment>
        <h4>What makes a great RepTool photo?</h4>
        <ol>

          <li>
            <h6>Pictures should be clear and focused on the animal</h6>
            <p>While we love to see how much you love your pets we'd very much like the subject of the photo
              to be primarily of the animal.</p>
          </li>
          <li>
            <h6>Pictures should be accurate to the morph specified</h6>
            <p>The browse morph feature is to aid other reptile keepers just like you to be able to visually
              identify their own pets. RepTool may change the morph you specify if another morph is
              believed to be correct. </p>
          </li>
          <li>
            <h6>Be sure to credit your photos how you want other users to see!</h6>
            <p>Remember these photos will be seen by other users, making it a great opprotunity to show off
              your photography skills or even give your breeding projects some attention!</p>
          </li>
          <li>
            <h6>But most importantly, thank you for your help!</h6>
            <p>Due to the enormous amount of morphs/species out there RepTool relies heavily on the
              contributions of it's users. If at any time you'd like to remove your photos, just email us
              at <a href="mailto:contact@coldbloodedstudios.com">contact@coldbloodedstudios.com</a></p>
          </li>
        </ol>
      </React.Fragment>
    )} />
  )
}